import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { asyncForEach, asyncReturnEach } from '../../libs/async';
import { Apartment } from '../../types/apartments';
import { ApartmentsBucketList, BucketObject, UploadFileToS3Response } from './types';

const uploadApartmentImageFileToS3 = async (file: any, apartmentId: string): Promise<UploadFileToS3Response> => {
    const filename = file.name;
    const fileExtension = filename.split('.').pop();
    const generatedFilename = `${uuidv4()}.${fileExtension}`;
    const key = `apartments/${apartmentId}/${generatedFilename}`;
    try {
        await uploadData({
            path: key,
            data: file,
            options: {
                contentType: file.type,
            }
        }).result;
        return {
            filename,
            key
        };
    }
    catch (error) {
        throw new Error(`Failed to upload ${file} to storage`);
    }
};

const uploadFileToS3 = async (file: any, _bucket: string, _region: string): Promise<any> => {
    const fileExtension = file.type.split("/")[1];
    const generatedFilename = `${uuidv4()}.${fileExtension}`;
    const key = `profilePictures/${generatedFilename}`;
    try {
        await uploadData({
            path: key,
            data: file,
            options: {
                contentType: file.type,
            }
        }).result;
        return {
            key
        };
    }
    catch (error) {
        console.log(error);
        throw new Error(`Failed to upload ${file} to storage`);
    }
};

const getFileURL = async (key: string): Promise<string> => {
    const result = await getUrl({
        path: key,
    });
    return result.url.toString();
};

/**
 * Derives the thumbnail S3 key for a given original key.
 * profilePictures/{uuid}.jpg  -> profilePictures/thumb/{uuid}.webp
 * apartments/{id}/{uuid}.jpg  -> apartments/{id}/thumb/{uuid}.webp
 */
const getThumbKey = (key: string): string => {
    const parts = key.split('/');
    const filename = parts.pop()!.replace(/\.[^.]+$/, '') + '.webp';
    return [...parts, 'thumb', filename].join('/');
};

const getThumbURL = async (key: string): Promise<string> => {
    return getFileURL(getThumbKey(key));
};

const deleteFile = async (key: string): Promise<string> => {
    await remove({
        path: key,
    });
    return key;
};

const getApartmentMediaObjects = async (apartmentId: string): Promise<BucketObject[]> => {
    const result = await list({
        path: `apartments/${apartmentId}/`,
    });
    // Map v6 items (with `path`) to our BucketObject interface (with `key`)
    // Filter out /thumb/ files — those are auto-generated thumbnails
    return result.items
        .filter((item: any) => !item.path.includes('/thumb/'))
        .map((item: any) => ({
            key: item.path,
            eTag: item.eTag,
            lastModified: item.lastModified,
            size: item.size,
        }));
};

const appendObjectListWithUrl = async (objects: BucketObject[]) => {
    const objectsWithUrl = await asyncReturnEach(objects, async (object: BucketObject) => {
        const [url, thumbUrl] = await Promise.all([
            getFileURL(object.key),
            getThumbURL(object.key).catch((): undefined => undefined),
        ]);
        return {
            ...object,
            url,
            thumbUrl,
        };
    });
    return objectsWithUrl;
};

const getAllMediaObjects = async (apartments: Apartment[]): Promise<ApartmentsBucketList> => {
    let allMediaByApartmentId: ApartmentsBucketList = [];
    await asyncForEach(apartments, async ({ apartmentId, media, metadata }: Apartment) => {
        if (metadata.media) {
            const result = await list({
                path: `apartments/${apartmentId}/`,
            });
            let apartmentObjects: BucketObject[] = result.items
                .filter((item: any) => !item.path.includes('/thumb/'))
                .map((item: any) => ({
                    key: item.path,
                    eTag: item.eTag,
                    lastModified: item.lastModified,
                    size: item.size,
                }));
            let sortedApartmentObjects = apartmentObjects;
            if (Array.isArray(media) && media.length && apartmentObjects) {
                sortedApartmentObjects = media
                    .map((mediaItem) => {
                    return (apartmentObjects.find((apartmentObject) => apartmentObject.key === mediaItem.key) || ({} as BucketObject));
                })
                    .filter((file) => Boolean(file.key));
            }
            const objectsWithUrl = await appendObjectListWithUrl(sortedApartmentObjects);
            allMediaByApartmentId = [
                ...allMediaByApartmentId,
                [apartmentId, objectsWithUrl]
            ];
        }
    });
    return allMediaByApartmentId;
};

const uploadFileUsingPreSignedURL = async (preSignedURL: string, file: any) => {
    const responseData: any = await fetch(preSignedURL, {
        method: 'put',
        body: file
    });
    return responseData;
};

export { uploadApartmentImageFileToS3, uploadFileToS3, getFileURL, getThumbURL, getThumbKey, deleteFile, getApartmentMediaObjects, getAllMediaObjects, uploadFileUsingPreSignedURL };
